import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { Mailbox } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';

export function useMailboxes() {
  return useQuery({ queryKey: qk.mailboxes, queryFn: () => api('GET /api/mailboxes'), staleTime: 30_000 });
}

export function useMailboxMap(): Map<string, Mailbox> {
  const { data } = useMailboxes();
  const m = new Map<string, Mailbox>();
  data?.mailboxes.forEach((mb) => m.set(mb.id, mb));
  return m;
}

export function useFolders(mailboxId: string | null | undefined) {
  return useQuery({
    queryKey: qk.folders(mailboxId ?? 'none'),
    queryFn: () => api('GET /api/mailboxes/:id/folders', { params: { id: mailboxId! } }),
    enabled: !!mailboxId,
  });
}

/** Unread counts of the inbox folder per mailbox (used by the dock badge and the account strip). */
export function useInboxUnreadByMailbox(): Map<string, number> {
  const list = useMailboxes().data?.mailboxes ?? [];
  const folders = useQueries({
    queries: list.map((m) => ({
      queryKey: qk.folders(m.id),
      queryFn: () => api('GET /api/mailboxes/:id/folders', { params: { id: m.id } }),
      staleTime: 60_000,
    })),
  });
  const key = folders.map((q) => q.data?.folders.filter((f) => f.role === 'inbox').map((f) => `${f.mailboxId}:${f.unreadCount}`).join(',') ?? '').join('|');
  return useMemo(() => {
    const m = new Map<string, number>();
    for (const q of folders) for (const f of q.data?.folders ?? []) if (f.role === 'inbox') m.set(f.mailboxId, (m.get(f.mailboxId) ?? 0) + f.unreadCount);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function useInboxUnread(mailboxId?: string | null): number {
  const by = useInboxUnreadByMailbox();
  if (mailboxId) return by.get(mailboxId) ?? 0;
  let n = 0;
  by.forEach((v) => (n += v));
  return n;
}
